import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ComplianceStatusBadge from './ComplianceStatusBadge';

const SitesTable = ({ sites, onViewSite }) => {
  const navigate = useNavigate();

  const handleViewSite = (siteId) => {
    if (onViewSite) {
      onViewSite(siteId);
    } else {
      navigate('/site-dashboard', { state: { siteId } });
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 border border-border overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">Site Name</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">Client</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">Compliance</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">Workers</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">Start Date</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">End Date</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground font-heading">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sites?.map((site) => (
              <tr key={site?.id} className="hover:bg-muted/50 transition-smooth">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                      <Icon name="Building2" size={20} color="var(--color-accent)" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{site?.site_name}</p>
                      <p className="text-sm text-muted-foreground">{site?.address}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-foreground">{site?.client_name}</p>
                </td>
                <td className="px-6 py-4">
                  <ComplianceStatusBadge status={site?.compliance_status} />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-foreground font-medium">{site?.worker_count || 0}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-foreground whitespace-nowrap">
                    {site?.start_date ? new Date(site.start_date)?.toLocaleDateString() : 'N/A'}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <p className="text-foreground whitespace-nowrap">
                    {site?.end_date ? new Date(site.end_date)?.toLocaleDateString() : 'N/A'}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewSite(site?.id)}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {sites?.map((site) => (
          <div key={site?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg flex-shrink-0">
                  <Icon name="Building2" size={20} color="var(--color-accent)" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground truncate">{site?.site_name}</h4>
                  <p className="text-sm text-muted-foreground line-clamp-1">{site?.address}</p>
                </div>
              </div>
              <ComplianceStatusBadge status={site?.compliance_status} />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="text-xs text-muted-foreground font-caption mb-1">Client</p>
                <p className="text-sm text-foreground font-medium">{site?.client_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-caption mb-1">Workers</p>
                <div className="flex items-center gap-1">
                  <Icon name="Users" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-foreground font-medium">{site?.worker_count || 0}</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-caption mb-1">Start Date</p>
                <p className="text-sm text-foreground">
                  {site?.start_date ? new Date(site.start_date)?.toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-caption mb-1">End Date</p>
                <p className="text-sm text-foreground">
                  {site?.end_date ? new Date(site.end_date)?.toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewSite(site?.id)}
              iconName="ArrowRight"
              iconPosition="right"
              fullWidth
            >
              View Site Details
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SitesTable;